import { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { Home } from './pages/Home';
import { Features } from './pages/Features';
import { Contact } from './pages/Contact';
import { AiDlp } from './pages/content/AiDlp';
import { ShadowAi } from './pages/content/ShadowAi';
import { StopChatgptDataLeaks } from './pages/content/StopChatgptDataLeaks';
import { AiDlpVsTraditionalDlp } from './pages/content/AiDlpVsTraditionalDlp';
import { metaForPath, canonicalFor } from './seo';

function upsertMeta(selector: string, attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

// Keeps document metadata in sync during client-side SPA navigation. The
// initial (prerendered) HTML already ships correct tags; this handles route
// changes after hydration.
function useSeo() {
  const { pathname } = useLocation();
  useEffect(() => {
    const meta = metaForPath(pathname);
    const canonical = canonicalFor(pathname);
    document.title = meta.title;
    upsertMeta('meta[name="description"]', 'name', 'description', meta.description);
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', meta.title);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', meta.description);
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', canonical);
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', meta.title);
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', meta.description);
    upsertCanonical(canonical);
  }, [pathname]);
}

// Router-agnostic app shell. Rendered under BrowserRouter on the client
// (App.tsx) and under StaticRouter at build time (entry-server.tsx).
export function AppShell() {
  useSeo();

  return (
    <div className="relative flex min-h-screen flex-col">
      <img
        src="/web.svg"
        alt=""
        aria-hidden="true"
        className="pointer-events-none fixed -left-5 -top-5 h-[700px] w-[700px] select-none opacity-[0.12]"
        style={{ maskImage: 'radial-gradient(circle at 0% 0%, black 25%, transparent 75%)', WebkitMaskImage: 'radial-gradient(circle at 0% 0%, black 25%, transparent 75%)' }}
      />
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/features" element={<Features />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/ai-dlp" element={<AiDlp />} />
          <Route path="/shadow-ai" element={<ShadowAi />} />
          <Route path="/stop-chatgpt-data-leaks" element={<StopChatgptDataLeaks />} />
          <Route path="/ai-dlp-vs-traditional-dlp" element={<AiDlpVsTraditionalDlp />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}
